import dayjs from 'dayjs';
import { BackupFrequency, BackupSetting } from '../../../../../@types/backup';

export function formatBackupCreated(iso: string): string {
    const d = dayjs(iso);
    if (!d.isValid()) return '-';
    return d.format('MMM DD, YYYY · HH:mm');
}

/**
 * Returns an i18n key suffix for the frequency label.
 * Combine with the `cloudComputeInstance:backup.frequency.<key>` namespace.
 */
export function frequencyLabelKey(
    freq?: BackupFrequency
): 'autoDaily' | 'autoWeekly' | 'autoMonthly' {
    if (freq === 'daily') return 'autoDaily';
    if (freq === 'monthly') return 'autoMonthly';
    return 'autoWeekly';
}

function startingHour(starting_at?: string): number {
    if (!starting_at) return 3;
    const d = dayjs(starting_at);
    if (!d.isValid()) return 3;
    return d.hour();
}

function weeklyTargetDays(s: BackupSetting): number[] {
    const map: Array<[keyof BackupSetting, number]> = [
        ['on_sunday', 0],
        ['on_monday', 1],
        ['on_tuesday', 2],
        ['on_wednesday', 3],
        ['on_thursday', 4],
        ['on_friday', 5],
        ['on_saturday', 6],
    ];
    const flagged = map.filter(([k]) => Boolean(s[k])).map(([, n]) => n);
    return flagged.length > 0 ? flagged : [0];
}

export function computeNextRun(s?: BackupSetting): string {
    if (!s) return '-';
    const hour = startingHour(s.starting_at);
    const now = dayjs();
    let next = now.startOf('day').hour(hour);

    if (s.frequency === 'daily') {
        if (!next.isAfter(now)) next = next.add(1, 'day');
    } else if (s.frequency === 'monthly') {
        const dayOfMonth = s.on_day && s.on_day > 0 ? s.on_day : 1;
        next = now.startOf('month').date(dayOfMonth).hour(hour);
        if (!next.isAfter(now)) next = next.add(1, 'month');
    } else {
        const targets = weeklyTargetDays(s);
        let best: dayjs.Dayjs | null = null;
        for (let i = 0; i < 14; i++) {
            const candidate = now.startOf('day').add(i, 'day').hour(hour);
            if (!candidate.isAfter(now)) continue;
            if (targets.includes(candidate.day())) {
                best = candidate;
                break;
            }
        }
        if (best) next = best;
    }

    return next.format('ddd, MMM D · HH:mm');
}

export function formatHourOption(hour: number): string {
    return `${String(hour).padStart(2, '0')}:00 UTC+7`;
}

export function isoForHour(hour: number): string {
    const today = dayjs();
    const yyyy = today.format('YYYY-MM-DD');
    return dayjs(`${yyyy}T${String(hour).padStart(2, '0')}:00:00+07:00`).toISOString();
}
